import { jsx as _jsx } from "react/jsx-runtime";
import { createLogger } from '@svs/nova-core/log';
import { SingleRowGroup } from '@svs/ui/react/bet-button';
import { useMemo } from 'react';
import { isOutcome, outcomeStringToIndex } from '../../schema/coupon/entry.js';
import { cn } from '../utils/entry.js';
import convertOutcomeCountToLabels from './convertOutcomeCountToLabels.js';
const logger = createLogger('poolbetting.coupon');
/**
 * This component is responsible for rendering the correct amount of outcomes and converting the outcome count to correct labels.
 *
 * @param root0 Component that displays the outcome.
 * @param root0.className Classname for the component
 * @param root0.selectedOutcomes Array with selected outcomes
 * @param root0.outcomeCount Number of outcomes
 * @param root0.renderItem Function to render the outcome
 * @param root0.eventNumber Unique identifier for the event
 * @param root0.couponIndex Index of the coupon containing this event
 * @param root0.onChange Callback function triggered when an outcome is selected
 * @param root0.localDistribution Array of local distribution values for outcomes
 * @returns Component that displays the outcome.
 */
export default function CouponEventOutcomesSrow({
  className,
  outcomeCount,
  selectedOutcomes,
  renderItem,
  localDistribution,
  eventNumber,
  couponIndex,
  onChange
}) {
  const outcomeLabels = useMemo(() => convertOutcomeCountToLabels(outcomeCount, localDistribution), [outcomeCount, localDistribution]);
  const defaultOutcome = selectedOutcomes && selectedOutcomes[0] !== undefined ? outcomeLabels[selectedOutcomes[0]]?.outcome : undefined;
  // z-index fix for betbuttons since overlapping link-area causes issues with clicking when in multi coupon mode in desktop
  // `[&>div]:my-0 [&>div]:py-0` neutralizes the `-my-2 py-2` on each BetButton wrapper so the
  // outcomes row hugs the buttons instead of inheriting the extra ~16px from packages/ui.
  return _jsx(SingleRowGroup, {
    className: cn('gap-x z-2 w-fit justify-items-center [grid-area:betButtons] lg:min-w-[150px] lg:[&_button]:w-full [&>div]:my-0 [&>div]:py-0 lg:[&>div]:w-full', className),
    value: defaultOutcome,
    onValueChange: onChange ? value => {
      const outcomeIndex = outcomeStringToIndex[value];
      if (!isOutcome(outcomeIndex)) {
        logger.warn('Invalid outcome value', {
          value,
          eventNumber,
          couponIndex
        });
        return;
      }
      onChange(outcomeIndex, couponIndex);
    } : undefined,
    children: outcomeLabels.map(({
      ariaLabel,
      outcome
    }, index) => renderItem({
      isSelected: selectedOutcomes?.length ? selectedOutcomes.includes(index) : false,
      outcome,
      index,
      isEmptyOutcome: !outcome && !ariaLabel
    }))
  });
}